import { Avatar as AvatarC, type AvatarProps as AvatarCProps, Flex } from '@chakra-ui/react'

import type { AvatarUserFragment } from '@/client/services/hasura/generated/graphql'
import { ACHIEVEMENT_ID_TO_DATA } from '@/client/services/progression'
import { getPublicUrl } from '@/client/services/url'

import { useGlobalContext } from '@/components/Worlds/context'
import type { UserDetails } from '@/components/Worlds/types'

import AvatarBase from './internal/AvatarBase'
import AvatarOnlineBadge from './internal/AvatarOnlineBadge'

const AVATAR_SCALE_PERCENTAGE = '78.225%'

// sorts the levels by points, descending
const LEVEL_POINTS = Object.entries(ACHIEVEMENT_ID_TO_DATA)
  .filter(([key]) => key.substring(0, 5) === 'level')
  .map(entry => ({ level: entry[0], criteriaValue: entry[1].criteriaValue }))
  .sort((a, b) => b.criteriaValue - a.criteriaValue)

export default function Avatar({
  user,
  size,
  hideFrame = false,
  hideOnlineBadge = false,
  ...avatarCProps
}: {
  user: Pick<UserDetails, 'id' | 'displayName' | 'avatarUrl' | 'points'> | AvatarUserFragment
  size: AvatarCProps['size']
  hideFrame?: boolean
  hideOnlineBadge?: boolean
} & AvatarCProps): JSX.Element {
  const { onlineUsers } = useGlobalContext()

  const isOnline = onlineUsers.some(onlineUser => onlineUser.id === user.id)

  // finds the highest level for this user's point total
  const highestLevel = LEVEL_POINTS.find(
    ({ criteriaValue }) =>
      (('virtual_progression_points' in user ? user.virtual_progression_points : user.points) ??
        0) >= criteriaValue
  )?.level

  if (highestLevel == null || hideFrame === true) {
    return (
      <AvatarBase user={user} size={size} {...avatarCProps}>
        {isOnline && !hideOnlineBadge ? <AvatarOnlineBadge /> : null}
      </AvatarBase>
    )
  }

  const frameUrl = `${getPublicUrl()}/avatarFrames/${highestLevel}.svg`

  return (
    <Flex align="center" justify="space-between">
      <AvatarC
        bg="none"
        alignSelf="center"
        size={size}
        src={frameUrl}
        name={highestLevel.toString()}
        {...avatarCProps}
      >
        <AvatarBase
          user={user}
          size={size}
          alignSelf="center"
          position="absolute"
          width={AVATAR_SCALE_PERCENTAGE}
          height={AVATAR_SCALE_PERCENTAGE}
        />
        {isOnline && !hideOnlineBadge ? <AvatarOnlineBadge /> : null}
      </AvatarC>
    </Flex>
  )
}
